import { PromotionComponentType } from 'src/models/promotion/PromotionTypes';

/**
 * Resolving pasted component NAMES to the ids a bulk promotion submits.
 *
 * MDA and TPA components are addressed by opaque ids — a UUID, or a generated slug like
 * `p3lib-godr-tpa-ws-SnYns7` — while the name an operator knows them by is something else entirely
 * ("Catalogue MDA Sheet-RzTrQE", "Kuiper_B926", "fc meadowlark"). Neither is derivable from the other, so
 * pasting what you can read off a screen meant every row came back NOT_FOUND. This maps one to the other.
 *
 * Deliberately NOT part of parseBulkIdentifiers. That function is pure and synchronous, and every existing
 * paste behaviour is pinned to it; name lookup needs data fetched over the network. Keeping it separate means
 * an id-only paste takes exactly the path it always did, and this cannot regress it.
 *
 * Only the four types with a list endpoint are supported. CLI_CONFIG and PLI_CONFIG have none, and need none:
 * their ids ARE the human-readable identifier (`00017338.0_01_B904-7V`).
 */

/** One candidate a name could refer to. */
export interface NamedComponent {
    componentId: string;
    name: string;
    /** The source's own status. Used only to prefer an active candidate when a name is ambiguous. */
    status?: string;
}

export enum NameResolution {
    /** The value was already a known id, so nothing was looked up. */
    WAS_AN_ID = 'WAS_AN_ID',
    /** Exactly one component carries this name. */
    RESOLVED = 'RESOLVED',
    /** Several components carry this name; the operator has to disambiguate. */
    AMBIGUOUS = 'AMBIGUOUS',
    /** Neither a known id nor a known name. Left as pasted, so the service reports on it. */
    UNKNOWN = 'UNKNOWN',
}

export interface ResolvedName {
    /** Exactly what was pasted, unmodified, so the summary can show it back. */
    input: string;
    resolution: NameResolution;
    /** The id to submit. Absent for AMBIGUOUS and UNKNOWN — neither has one answer. */
    componentId?: string;
    /** For AMBIGUOUS: every candidate, so the operator can pick by id instead. */
    candidates?: NamedComponent[];
    message?: string;
}

/** The types a name can be resolved for. The other two are addressed by a readable id already. */
export const NAME_RESOLVABLE_TYPES: ReadonlySet<string> = new Set([
    PromotionComponentType.MDA_SHEET,
    PromotionComponentType.MDA_WORKSHEET,
    PromotionComponentType.TPA_TEMPLATE,
    PromotionComponentType.TPA_WORKSHEET,
]);

export function isNameResolvable(componentType?: string | null): boolean {
    return componentType != null && NAME_RESOLVABLE_TYPES.has(componentType);
}

/**
 * Names are compared case-insensitively and with runs of whitespace collapsed.
 *
 * An operator copies a name out of a table cell or a wiki page, which is where stray double spaces, a
 * trailing space and a different capitalisation come from. Requiring an exact match would refuse the name
 * it is looking at. Deliberately NOT stripping punctuation: hyphens and underscores carry meaning in these
 * names ("ADCI - Feb26", "Test_MDA_notifications1"), and two components can differ only by them.
 */
export function normaliseName(name: string): string {
    return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Builds the name index. Several components CAN share a name — nothing enforces uniqueness at write time —
 * so every entry holds a list rather than one id, and ambiguity is reported rather than guessed at.
 *
 * One component listed twice is NOT ambiguity, and the list routinely holds it. TPAllocationWorksheetDao's
 * listWorksheetTemplates only collapses a template's versions when one of them is the CURRENT_VERSION, so a
 * template with no active version — a pure draft — arrives once per version row, every row carrying the same
 * templateId. Counted naively that name has two candidates, and because
 * GetTemplatesAndWorksheetsComponent's WorksheetTemplate builder never sets a status, activeAmong below
 * cannot break the tie either. The result was a draft template reported as ambiguous against itself, with
 * the same id offered twice as the alternative to itself.
 *
 * So the same componentId is only ever recorded once per name. Distinct components sharing a name are still
 * reported ambiguous, which is the case this list exists for.
 */
export function buildNameIndex(components: NamedComponent[]): Map<string, NamedComponent[]> {
    const index = new Map<string, NamedComponent[]>();
    for (const component of components) {
        if (!component || !component.name || !component.componentId) {
            continue;
        }
        const key = normaliseName(component.name);
        if (key.length === 0) {
            continue;
        }
        const existing = index.get(key);
        if (!existing) {
            index.set(key, [component]);
        } else if (!existing.some((seen) => seen.componentId === component.componentId)) {
            existing.push(component);
        }
    }
    return index;
}

/**
 * Narrows several candidates sharing a name to the one that is active, when exactly one is.
 *
 * Promotion refuses a component with no active version, so an inactive candidate is not a real alternative —
 * and a name that matches one active and two archived components has one sensible answer, not three.
 * Compared case-insensitively because the status vocabulary is inconsistent across these sources: MDA sheets
 * store "ACTIVE" while calculations store "Active".
 */
function activeAmong(candidates: NamedComponent[]): NamedComponent | null {
    const active = candidates.filter((c) => (c.status || '').toUpperCase() === 'ACTIVE');
    return active.length === 1 ? active[0] : null;
}

/**
 * Resolves one pasted value.
 *
 * An id always wins over a name. If someone pastes a value that is both a valid id and some other
 * component's name, the id is what they addressed — and it is the interpretation that worked before this
 * existed, so it cannot regress an existing paste.
 */
export function resolveOneName(
    input: string,
    nameIndex: Map<string, NamedComponent[]>,
    knownIds: ReadonlySet<string>
): ResolvedName {
    const trimmed = input.trim();
    if (knownIds.has(trimmed)) {
        return { input, resolution: NameResolution.WAS_AN_ID, componentId: trimmed };
    }

    const candidates = nameIndex.get(normaliseName(trimmed));
    if (!candidates || candidates.length === 0) {
        return {
            input,
            resolution: NameResolution.UNKNOWN,
            message: 'No component of this type has this ID or name in this environment.',
        };
    }
    if (candidates.length === 1) {
        return { input, resolution: NameResolution.RESOLVED, componentId: candidates[0].componentId };
    }

    const active = activeAmong(candidates);
    if (active) {
        return { input, resolution: NameResolution.RESOLVED, componentId: active.componentId };
    }
    return {
        input,
        resolution: NameResolution.AMBIGUOUS,
        candidates,
        message:
            `${candidates.length} components share this name. Paste the ID instead: `
            + candidates.map((c) => c.componentId).join(', '),
    };
}

/** Resolves a whole paste. Order is preserved, so a row here lines up with the row the operator sees. */
export function resolveNames(
    inputs: string[],
    components: NamedComponent[]
): { rows: ResolvedName[]; componentIds: string[] } {
    const nameIndex = buildNameIndex(components);
    const knownIds = new Set(components.map((c) => c.componentId).filter(Boolean));
    const rows = inputs.map((input) => resolveOneName(input, nameIndex, knownIds));
    /*
     * An unresolved value is still submitted, as pasted. The service is the authority on what exists, and
     * silently dropping a row would submit fewer components than the operator pasted — the failure mode that
     * matters most here. What the operator sees is the row's own outcome saying it was not recognised.
     */
    const componentIds = rows.map((row) => row.componentId || row.input.trim()).filter((id) => id.length > 0);
    return { rows, componentIds };
}
